import { lazy, Suspense } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { AppLayout } from './components/layout/AppLayout';
import { ProtectedRoute, PublicRoute } from './contexts/AuthContext';
import { LoadingSpinner } from './components/Common/LoadingSpinner';
// Wave 10 (Frontend audit C1): generic error boundary for every
// route group. Replaces the React default white-screen on render
// crashes with a friendly card + reload action.
import { RouteErrorBoundary } from './components/Common/RouteErrorBoundary';
// Wave-69: bookmark-migration fallback (slug-less URLs → /<slug>/...)
import { SlugRecover } from './components/SlugRecover';

// Lazy-loaded pages
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ProductList = lazy(() => import('./pages/Products/ProductList'));
const ProductCreate = lazy(() => import('./pages/Products/ProductCreate'));
const ProductEdit = lazy(() => import('./pages/Products/ProductEdit'));
const OrderList = lazy(() => import('./pages/Orders/OrderList'));
const OrderDetail = lazy(() => import('./pages/Orders/OrderDetail'));
const OrderPick = lazy(() => import('./pages/Grocery/OrderPick'));
const PayoutList = lazy(() => import('./pages/Payouts/PayoutList'));
const PayoutDetail = lazy(() => import('./pages/Payouts/PayoutDetail'));
const PayoutMethod = lazy(() => import('./pages/Payouts/PayoutMethod'));
const StatementList = lazy(() => import('./pages/Statements/StatementList'));
const StatementDetail = lazy(() => import('./pages/Statements/StatementDetail'));
const VendorAnalytics = lazy(() => import('./pages/Analytics/VendorAnalytics'));
const ReconciledAnalytics = lazy(() => import('./pages/Analytics/ReconciledAnalytics'));
const VendorProfile = lazy(() => import('./pages/Profile/VendorProfile'));
const AIChat = lazy(() => import('./pages/Support/AIChat'));
const SupportCases = lazy(() => import('./pages/Support/Cases'));
const ShopperCases = lazy(() => import('./pages/Support/ShopperCases'));
const InventoryList = lazy(() => import('./pages/Inventory/InventoryList'));
const OfferList = lazy(() => import('./pages/Offers/OfferList'));
const UnitPricing = lazy(() => import('./pages/Grocery/UnitPricing'));
const NotificationsPage = lazy(() => import('./pages/Notifications/NotificationsPage'));
const StorefrontPreview = lazy(() => import('./pages/Storefront/StorefrontPreview'));
const ReturnsList = lazy(() => import('./pages/Returns/ReturnsList'));
const DisputesList = lazy(() => import('./pages/Disputes/DisputesList'));
const DisputeDetail = lazy(() => import('./pages/Disputes/DisputeDetail'));
const CouponsList = lazy(() => import('./pages/Coupons/CouponsList'));
const Delivery = lazy(() => import('./pages/Delivery/Delivery'));
const Availability = lazy(() => import('./pages/Delivery/Availability'));
// WP1.1 — vendor delivery slot templates + pickup (BOPIS) locations.
const Slots = lazy(() => import('./pages/Delivery/Slots'));
const Menu = lazy(() => import('./pages/Menu/Menu'));
const Console = lazy(() => import('./pages/Console/Console'));
const ReviewsList = lazy(() => import('./pages/Reviews/ReviewsList'));
const QuestionsList = lazy(() => import('./pages/Questions/QuestionsList'));
// Wave-52: vendor-side AI surfaces
const AIForecast = lazy(() => import('./pages/AI/Forecast'));
const AIListingQuality = lazy(() => import('./pages/AI/ListingQuality'));
// Wave-75: admin↔vendor 1:1 messaging
const MessagesPage = lazy(() => import('./pages/Messages/MessagesPage'));
// Wave-89: per-vendor invoices
const InvoicesPage = lazy(() => import('./pages/Invoices/InvoicesPage'));
// Wave 8 Task 4: demo vendor impersonation consumer
const DemoSSO = lazy(() => import('./pages/Auth/DemoSSO'));
// Admin-as-vendor ("Manage as vendor") impersonation consumer
const AdminSSO = lazy(() => import('./pages/Auth/AdminSSO'));
// Admin-as-vendor change timeline (vendor-facing)
const AdminChanges = lazy(() => import('./pages/Activity/AdminChanges'));
// Wave 9: public vendor portal landing page (replaces redirect-to-login at root)
const PublicLanding = lazy(() => import('./pages/PublicLanding'));
// Wave 9.6: public vendor directory
const PublicBrowse = lazy(() => import('./pages/PublicBrowse'));
// Wave 9.6: vendor referral program
const Referrals = lazy(() => import('./pages/Referrals'));

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LoadingSpinner fullPage />}>{children}</Suspense>;
}

// Wave-69: every authenticated page lives under /:shop_slug/* so the
// URL always shows which marketplace the vendor is in. Pre-wave-69
// the auth tree was mounted at `/` (the SPA's basename) producing
// URLs like /vendors/dashboard — confusing for vendors with accounts
// on multiple marketplaces, breaks brand presence, and asymmetric
// with the public auth pages which already used the slug.
//
// Protected children paths are relative (no leading slash) so they
// inherit `/:shop_slug/` from the parent. Existing slug-less
// bookmarks are caught by the top-level `path: '*'` catch-all which
// hands off to SlugRecover for redirect.
const protectedChildren = [
  { path: 'dashboard', element: <SuspenseWrapper><Dashboard /></SuspenseWrapper> },
  { path: 'products', element: <SuspenseWrapper><ProductList /></SuspenseWrapper> },
  { path: 'products/new', element: <SuspenseWrapper><ProductCreate /></SuspenseWrapper> },
  { path: 'products/:id/edit', element: <SuspenseWrapper><ProductEdit /></SuspenseWrapper> },
  { path: 'orders', element: <SuspenseWrapper><OrderList /></SuspenseWrapper> },
  { path: 'orders/:id', element: <SuspenseWrapper><OrderDetail /></SuspenseWrapper> },
  { path: 'orders/:id/pick', element: <SuspenseWrapper><OrderPick /></SuspenseWrapper> },
  { path: 'payouts', element: <SuspenseWrapper><PayoutList /></SuspenseWrapper> },
  { path: 'payouts/:id', element: <SuspenseWrapper><PayoutDetail /></SuspenseWrapper> },
  { path: 'payouts/method', element: <SuspenseWrapper><PayoutMethod /></SuspenseWrapper> },
  { path: 'statements', element: <SuspenseWrapper><StatementList /></SuspenseWrapper> },
  { path: 'statements/:id', element: <SuspenseWrapper><StatementDetail /></SuspenseWrapper> },
  { path: 'inventory', element: <SuspenseWrapper><InventoryList /></SuspenseWrapper> },
  { path: 'offers', element: <SuspenseWrapper><OfferList /></SuspenseWrapper> },
  { path: 'grocery/unit-pricing', element: <SuspenseWrapper><UnitPricing /></SuspenseWrapper> },
  { path: 'notifications', element: <SuspenseWrapper><NotificationsPage /></SuspenseWrapper> },
  { path: 'activity', element: <SuspenseWrapper><AdminChanges /></SuspenseWrapper> },
  { path: 'storefront', element: <SuspenseWrapper><StorefrontPreview /></SuspenseWrapper> },
  { path: 'returns', element: <SuspenseWrapper><ReturnsList /></SuspenseWrapper> },
  { path: 'disputes', element: <SuspenseWrapper><DisputesList /></SuspenseWrapper> },
  { path: 'disputes/:id', element: <SuspenseWrapper><DisputeDetail /></SuspenseWrapper> },
  { path: 'coupons', element: <SuspenseWrapper><CouponsList /></SuspenseWrapper> },
  { path: 'delivery', element: <SuspenseWrapper><Delivery /></SuspenseWrapper> },
  { path: 'menu', element: <SuspenseWrapper><Menu /></SuspenseWrapper> },
  // WP1.2 — vendor operating attributes (hours / holidays / pause / capacity).
  { path: 'availability', element: <SuspenseWrapper><Availability /></SuspenseWrapper> },
  // WP1.1 — vendor delivery slot templates + pickup (BOPIS) locations.
  { path: 'slots', element: <SuspenseWrapper><Slots /></SuspenseWrapper> },
  { path: 'console', element: <SuspenseWrapper><Console /></SuspenseWrapper> },
  { path: 'reviews', element: <SuspenseWrapper><ReviewsList /></SuspenseWrapper> },
  { path: 'questions', element: <SuspenseWrapper><QuestionsList /></SuspenseWrapper> },
  { path: 'analytics', element: <SuspenseWrapper><VendorAnalytics /></SuspenseWrapper> },
  { path: 'analytics/reconciled', element: <SuspenseWrapper><ReconciledAnalytics /></SuspenseWrapper> },
  { path: 'profile', element: <SuspenseWrapper><VendorProfile /></SuspenseWrapper> },
  { path: 'referrals', element: <SuspenseWrapper><Referrals /></SuspenseWrapper> },
  { path: 'support', element: <SuspenseWrapper><AIChat /></SuspenseWrapper> },
  { path: 'support/cases', element: <SuspenseWrapper><SupportCases /></SuspenseWrapper> },
  { path: 'support/shopper-cases', element: <SuspenseWrapper><ShopperCases /></SuspenseWrapper> },
  // Wave-75: admin↔vendor 1:1 messaging
  { path: 'messages', element: <SuspenseWrapper><MessagesPage /></SuspenseWrapper> },
  // Wave-89: invoices
  { path: 'invoices', element: <SuspenseWrapper><InvoicesPage /></SuspenseWrapper> },
  // Wave-52: vendor-side AI surfaces.
  { path: 'ai/forecast', element: <SuspenseWrapper><AIForecast /></SuspenseWrapper> },
  { path: 'ai/listing-quality', element: <SuspenseWrapper><AIListingQuality /></SuspenseWrapper> },
];

export const router = createBrowserRouter(
  [
    // Slug-scoped routes — public + auth + authenticated all live under
    // `/:shop_slug/*`. The order of children matters: index first, then
    // specific public pages, then the protected sub-tree as a layout
    // route at the END so it acts as the fall-through for any
    // unmatched `/:shop_slug/something` path. (Strictly, react-router
    // resolves by pattern specificity, not order — but explicit order
    // mirrors the mental model.)
    {
      path: '/:shop_slug',
      errorElement: <RouteErrorBoundary />,
      children: [
        // Wave 9: public landing — branded recruit page at /:shop_slug
        { index: true, element: <SuspenseWrapper><PublicLanding /></SuspenseWrapper> },

        // Wave 9.6: public vendor directory
        { path: 'browse', element: <SuspenseWrapper><PublicBrowse /></SuspenseWrapper> },

        // Public auth pages (slug-scoped)
        {
          path: 'login',
          element: <PublicRoute><SuspenseWrapper><Login /></SuspenseWrapper></PublicRoute>,
        },
        {
          path: 'register',
          element: <PublicRoute><SuspenseWrapper><Register /></SuspenseWrapper></PublicRoute>,
        },
        {
          path: 'forgot-password',
          element: <SuspenseWrapper><ForgotPassword /></SuspenseWrapper>,
        },
        {
          path: 'reset-password',
          element: <SuspenseWrapper><ResetPassword /></SuspenseWrapper>,
        },
        {
          path: 'verify-email',
          element: <SuspenseWrapper><VerifyEmail /></SuspenseWrapper>,
        },
        // Wave 8 Task 4: demo SSO consumer (slug-scoped)
        {
          path: 'auth/demo-sso',
          element: <SuspenseWrapper><DemoSSO /></SuspenseWrapper>,
        },
        // Admin-as-vendor SSO consumer (slug-scoped)
        {
          path: 'auth/admin-sso',
          element: <SuspenseWrapper><AdminSSO /></SuspenseWrapper>,
        },

        // Wave-69: every authenticated page lives under /:shop_slug/*
        {
          element: (
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          ),
          children: protectedChildren,
        },
      ],
    },

    // Slug-less legacy paths (kept for backward compatibility +
    // bookmark migration). All of these either render a public page
    // that handles the no-slug state or hand off to SlugRecover.
    {
      path: '/login',
      element: <PublicRoute><SuspenseWrapper><Login /></SuspenseWrapper></PublicRoute>,
      errorElement: <RouteErrorBoundary />,
    },
    {
      path: '/register',
      element: <PublicRoute><SuspenseWrapper><Register /></SuspenseWrapper></PublicRoute>,
    },
    {
      path: '/forgot-password',
      element: <SuspenseWrapper><ForgotPassword /></SuspenseWrapper>,
    },
    {
      path: '/reset-password',
      element: <SuspenseWrapper><ResetPassword /></SuspenseWrapper>,
    },
    {
      path: '/verify-email',
      element: <SuspenseWrapper><VerifyEmail /></SuspenseWrapper>,
    },
    {
      path: '/auth/demo-sso',
      element: <SuspenseWrapper><DemoSSO /></SuspenseWrapper>,
    },
    {
      path: '/auth/admin-sso',
      element: <SuspenseWrapper><AdminSSO /></SuspenseWrapper>,
    },

    // Wave-69: top-level catch-all — any slug-less protected URL
    // (e.g. /dashboard, /orders/abc-123 — legacy bookmarks) is
    // funnelled to SlugRecover which looks up the remembered slug
    // from localStorage and redirects to /<slug>/<original-path>.
    // Replaces the prior `Navigate to="/dashboard"` which would
    // 404 under the new router shape (no top-level /dashboard).
    { path: '*', element: <SlugRecover /> },
  ],
  { basename: '/vendors' },
);
